/**
 * Tasks.
 *
 * Built against the mobile screen (taskdojo-app/src/screens/TasksScreen.js)
 * rather than adapted from the old web page, because the goal is one product on
 * two surfaces, not two products that share a database.
 *
 * What is deliberately identical to the phone:
 *
 *  - the header -- 30pt/900 title, rank badge, the raised add key;
 *  - the OPEN / OVERDUE / TODAY / DONE band, computed from the whole task list
 *    rather than the filtered view, so the figures describe the workload and
 *    not whatever tab is selected;
 *  - the filter set, *including* the conditional tabs. Assigned, Delegated and
 *    Declined only exist when they have something in them, which is why the row
 *    is built rather than written out;
 *  - the card: 15px radius, two-pixel border with a four-pixel bottom face, an
 *    8px colour rail down the left, a 16px inset, title at 16.5px/800, meta at
 *    12px/70%, and a 38px complete key pinned to the top right;
 *  - `isDone` reads `completed` OR `status === 'completed'`. Both fields exist
 *    and older documents carry one without the other, so testing either alone
 *    miscounts. The phone learnt this the hard way;
 *  - what you may do depends on what you are to the task: the creator may
 *    delete, the creator or assignee may edit and complete, anyone else may
 *    only stop watching. The security rules enforce this regardless, so
 *    offering a control that cannot succeed just produces a failed write and a
 *    confusing error.
 *
 * Dates never go through toLocaleDateString here. Everything user-facing is
 * formatted by ../../utils/datetime, which pins en-US exactly as the phone
 * does; every day comparison goes through `dayKey`, which is local. A visitor
 * in London and one in New York must read the same date off the same task.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useScore } from '../../hooks/useScore';
import BeltBadge from '../common/BeltBadge';
import {
  ScreenHeader, SummaryBand, FilterPill, ActionButton,
  PrimaryButton, SecondaryButton, Field, useInputClass, raised, pressable,
} from '../dojo';
import { formatDate, formatTime, toDateInput, toTimeInput, fromDateTimeInputs } from '../../utils/datetime';
import { taskCardTheme, chipText, readableOn, MARK_MIN } from '../../utils/taskColor';
import {
  Task, TASK_COLORS, createTask, updateTask, deleteTask,
} from '../../services/firebaseService';

// Categories deliberately are NOT a constant here. They used to be a frozen
// copy of the phone's defaults, so a category you created on the phone — or
// on an existing task — simply did not appear in this dropdown. AppContext
// already merges defaults, your profile's customs and anything found on your
// tasks; that is the list to show.

/** Mobile's getStatusColor, value for value. */
const STATUS_COLOR: Record<string, string> = {
  not_started: '#8E8E93',
  in_progress: '#4A6FA5',
  completed: '#4C9A2A',
  paused: '#C1763C',
  declined: '#E34234',
  pending: '#C1763C',
  draft: '#8E8E93',
};

/** Mobile's formatStatus. */
const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  paused: 'Paused',
  completed: 'Completed',
  draft: 'Draft',
  declined: 'Declined',
  pending: 'Pending',
};

const statusLabel = (s?: string) =>
  (s && STATUS_LABEL[s]) || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const isDone = (t: any) => !!(t?.completed || t?.status === 'completed');

/** Local midnight today, and tomorrow -- the boundaries every filter uses. */
const bounds = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { today, tomorrow };
};

type FilterId =
  | 'active' | 'assigned' | 'delegated' | 'declined'
  | 'overdue' | 'today' | 'watching' | 'completed' | 'all';

interface FormState {
  title: string;
  notes: string;
  category: string;
  color: string;
  status: Task['status'];
  tag: string;
  /** Free-text reference — a ticket number, an order id, whatever you use. */
  ref: string;
  /** Scheduled window. The phone offers both; without them here a task
      scheduled on mobile could not be seen or changed on the web. */
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  /** Minutes. The phone calls it duration and stores a plain number. */
  duration: number;
  /** Hidden from watchers and anyone the task is shared with. */
  isPrivate: boolean;
  deadlineDate: string;
  deadlineTime: string;
  progress: number;
  /** '' means nobody in particular, which the phone stores as your own uid. */
  assignedTo: string;
  watchers: string[];
  reminderDate: string;
  reminderTime: string;
}

const emptyForm = (): FormState => ({
  title: '', notes: '', category: 'General', color: TASK_COLORS[0].hex,
  status: 'not_started', tag: '', ref: '', deadlineDate: '', deadlineTime: '', progress: 0,
  startDate: '', startTime: '', endDate: '', endTime: '', duration: 0, isPrivate: false,
  assignedTo: '', watchers: [], reminderDate: '', reminderTime: '',
});

const Tasks: React.FC = () => {
  const { state, dispatch, allCategories } = useApp();
  const { connections } = useConnections();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';
  const score = useScore();
  const input = useInputClass();

  const uid = state.user?.uid;
  const tasks = (state.tasks || []) as Task[];

  const [filter, setFilter] = useState<FilterId>('active');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Task | null>(null);

  /** What the signed-in user is to a task, and therefore what they may do. */
  const roleOn = useCallback((t: Task) => {
    const isCreator = !!uid && t.createdBy === uid;
    const isAssignee = !!uid && t.assignedTo === uid;
    const isWatcher = !!uid && (t.watchers || []).includes(uid);
    return {
      isCreator, isAssignee, isWatcher,
      canWrite: isCreator || isAssignee,
      canDelete: isCreator,
      canLeave: !isCreator && (isAssignee || isWatcher),
    };
  }, [uid]);

  const nameFor = useCallback((id?: string) => {
    if (!id) return null;
    if (id === uid) return 'You';
    const c = connections.find((x: any) => x.uid === id);
    return c?.displayName || c?.email?.split('@')[0] || 'Someone';
  }, [connections, uid]);

  /* ---------------------------------------------------------------- *
   * Counts. Mobile's taskCounts, rule for rule: a done task counts only
   * as done, a declined one counts nowhere, and a task is overdue only
   * if its deadline is before today -- not merely before now, or
   * everything due at 09:00 would turn red by lunchtime.
   * ---------------------------------------------------------------- */
  const counts = useMemo(() => {
    const { today, tomorrow } = bounds();
    let open = 0, overdue = 0, dueToday = 0, done = 0;

    tasks.forEach((t) => {
      if (isDone(t)) { done += 1; return; }
      if (t.status === 'declined') return;
      open += 1;
      if (!t.deadline) return;
      const d = new Date(t.deadline);
      if (Number.isNaN(d.getTime())) return;
      if (d >= today && d < tomorrow) dueToday += 1;
      else if (d < today) overdue += 1;
    });

    return { open, overdue, today: dueToday, done };
  }, [tasks]);

  /* ---------------------------------------------------------------- *
   * The filters. One predicate per tab, used for both the list and the
   * tab's own count -- on the phone those are two separate expressions
   * and they disagree about whether a watched task is "active", so the
   * badge says one number and the list shows another. One definition
   * here, so they cannot drift.
   * ---------------------------------------------------------------- */
  const predicates = useMemo(() => {
    const { today, tomorrow } = bounds();
    const due = (t: Task) => (t.deadline ? new Date(t.deadline) : null);

    return {
      active: (t: Task) =>
        !isDone(t) && t.status !== 'declined' &&
        (!t.assignedTo || t.assignedTo === uid || t.status === 'accepted' ||
          (t.watchers || []).includes(uid!)),
      assigned: (t: Task) => t.assignedTo === uid && t.status === 'pending',
      delegated: (t: Task) => t.createdBy === uid && !!t.assignedTo && t.assignedTo !== uid,
      declined: (t: Task) => t.status === 'declined' && t.createdBy === uid,
      overdue: (t: Task) => {
        if (isDone(t)) return false;
        const d = due(t);
        return !!d && !Number.isNaN(d.getTime()) && d < today;
      },
      today: (t: Task) => {
        const d = due(t);
        return !!d && d >= today && d < tomorrow;
      },
      watching: (t: Task) => (t.watchers || []).includes(uid!) && !isDone(t),
      completed: (t: Task) => isDone(t),
      all: (_t: Task) => true,
    } as Record<FilterId, (t: Task) => boolean>;
  }, [uid]);

  const countOf = useCallback(
    (id: FilterId) => tasks.filter(predicates[id]).length,
    [tasks, predicates],
  );

  /** Tabs present unconditionally, and the three that appear only when used. */
  const tabs = useMemo(() => {
    const out: Array<{ id: FilterId; label: string; count: number; highlight?: boolean }> = [
      { id: 'active', label: 'Active', count: countOf('active') },
    ];
    const conditional: Array<[FilterId, string, boolean]> = [
      ['assigned', 'Assigned', true],
      ['delegated', 'Delegated', false],
      ['declined', 'Declined', false],
    ];
    conditional.forEach(([id, label, highlight]) => {
      const n = countOf(id);
      if (n > 0) out.push({ id, label, count: n, highlight });
    });
    (['overdue', 'today', 'watching', 'completed', 'all'] as FilterId[]).forEach((id) => {
      out.push({
        id,
        label: id.charAt(0).toUpperCase() + id.slice(1),
        count: countOf(id),
      });
    });
    return out;
  }, [countOf]);

  // A conditional tab can vanish while it is selected -- accept an assignment
  // and Assigned empties -- which would otherwise leave the page on a tab that
  // is no longer in the row, showing nothing and offering no way back.
  const activeFilter: FilterId = tabs.some((t) => t.id === filter) ? filter : 'active';

  const visible = useMemo(
    () => tasks.filter(predicates[activeFilter]),
    [tasks, predicates, activeFilter],
  );

  /* ----------------------------- writes ----------------------------- */

  const toggleComplete = async (task: Task) => {
    if (busyId) return;
    setBusyId(task.id);
    const next = !isDone(task);
    try {
      // updateTask derives status and completedAt from `completed`, so passing
      // the one field keeps the document internally consistent.
      await updateTask(task.id, { completed: next });
      dispatch({
        type: 'UPDATE_TASK',
        payload: { id: task.id, updates: { completed: next, status: next ? 'completed' : 'not_started' } },
      });
    } catch (e: any) {
      setError(e?.message || "Couldn't save that. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setCreating(true);
    setError('');
  };

  const openEdit = (task: Task) => {
    setForm({
      title: task.title || '',
      notes: task.notes || '',
      category: task.category || 'General',
      color: task.color || TASK_COLORS[0].hex,
      status: task.status || 'not_started',
      tag: task.tag || '',
      ref: task.ref || '',
      deadlineDate: toDateInput(task.deadline),
      deadlineTime: toTimeInput(task.deadline),
      startDate: toDateInput(task.startDateTime),
      startTime: toTimeInput(task.startDateTime),
      endDate: toDateInput(task.endDateTime),
      endTime: toTimeInput(task.endDateTime),
      duration: task.duration || 0,
      isPrivate: !!task.isPrivate,
      progress: task.progress || 0,
      // Stored as your own uid when unassigned; shown as "Me" either way.
      assignedTo: task.assignedTo && task.assignedTo !== uid ? task.assignedTo : '',
      watchers: task.watchers || [],
      reminderDate: toDateInput(task.reminder),
      reminderTime: toTimeInput(task.reminder),
    });
    setEditing(task);
    setCreating(false);
    setError('');
  };

  const closeModal = () => {
    setEditing(null);
    setCreating(false);
    setError('');
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Give the task a title.');
      return;
    }
    if (!uid) return;

    setSaving(true);
    setError('');
    try {
      // A time without a date means nothing, so the date drives it; a date
      // without a time is stored at local midnight.
      const deadline = fromDateTimeInputs(form.deadlineDate, form.deadlineTime);
      const startDateTime = fromDateTimeInputs(form.startDate, form.startTime);
      const endDateTime = fromDateTimeInputs(form.endDate, form.endTime);
      // A reminder is an absolute instant, so it is timezone-independent --
      // onTaskCreated/onTaskUpdated schedule straight off it. Changing it here
      // cancels and reschedules the pending notification, which is why the
      // value has to round-trip exactly rather than being rebuilt.
      const reminder = fromDateTimeInputs(form.reminderDate, form.reminderTime);
      const payload: Partial<Task> = {
        title: form.title.trim(),
        notes: form.notes.trim(),
        category: form.category,
        color: form.color,
        tag: form.tag.trim() || null,
        ref: form.ref.trim() || null,
        startDateTime,
        endDateTime,
        duration: Number(form.duration) || 0,
        isPrivate: form.isPrivate,
        deadline,
        progress: form.progress,
        // The phone writes both on create and on update, and does not
        // recompute status when an existing task is reassigned -- only
        // createTask derives 'pending'. Matched deliberately.
        assignedTo: form.assignedTo || uid,
        watchers: form.watchers,
        reminder,
      };

      if (editing) {
        await updateTask(editing.id, { ...payload, status: form.status });
        dispatch({
          type: 'UPDATE_TASK',
          payload: { id: editing.id, updates: { ...payload, status: form.status } },
        });
      } else {
        await createTask(payload, uid);
        // No optimistic insert: createTask writes through the same collection
        // the live listener is on, so the task arrives on its own. Adding it
        // here as well is how a new task ends up on screen twice.
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (task: Task) => {
    setSaving(true);
    try {
      await deleteTask(task.id);
      dispatch({ type: 'REMOVE_TASK', payload: task.id });
      setConfirmDelete(null);
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- render ----------------------------- */

  const modalOpen = creating || !!editing;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Tasks"
          right={<BeltBadge score={score.score} size={44} />}
          action={
            <ActionButton label="New task" onClick={openCreate}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: counts.open, label: 'Open' },
            { value: counts.overdue, label: 'Overdue', color: counts.overdue > 0 ? '#E34234' : undefined },
            { value: counts.today, label: 'Today', color: '#C08A1E' },
            { value: counts.done, label: 'Done', color: '#4C9A2A' },
          ]}
        />

        {/* Horizontal scroll rather than wrap: the row is a strip on the phone
            and stays one on a narrow browser window. */}
        <div className="flex gap-2 px-5 pb-4 overflow-x-auto">
          {tabs.map((t) => (
            <FilterPill
              key={t.id}
              active={activeFilter === t.id}
              highlight={t.highlight}
              count={t.count}
              onClick={() => setFilter(t.id)}
            >
              {t.label}
            </FilterPill>
          ))}
        </div>

        {error && !modalOpen && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px]`}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" className={colors.textMuted}>
                  <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">
                {activeFilter === 'active' ? 'Nothing on' : 'Nothing here'}
              </h2>
              <p className={`text-[15px] font-semibold ${colors.textSecondary}`}>
                {activeFilter === 'active'
                  ? 'Add a task and it shows up here.'
                  : 'No tasks match this filter.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-[10px] list-none p-0 m-0">
              {visible.map((task) => {
                const role = roleOn(task);
                const done = isDone(task);
                const { today } = bounds();
                const deadline = task.deadline ? new Date(task.deadline) : null;
                const overdue = !!deadline && !done && deadline < today;
                // Lifted until it actually clears 3:1 on this card -- several
                // of the task colours do not, unadjusted.
                const rail = readableOn(
                  taskCardTheme(task.color || '#4A6FA5', isDark).accent, card, MARK_MIN,
                );
                const assignee = task.assignedTo && task.assignedTo !== uid ? nameFor(task.assignedTo) : null;
                const watchers = (task.watchers || []).length;

                return (
                  <li key={task.id}>
                    {/* The whole card opens the task; the complete key inside
                        stops the click, exactly as on the phone. */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => openEdit(task)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(task); }
                      }}
                      className={`rounded-[15px] border-2 border-b-4 ${colors.card} ${colors.cardBorder}
                        p-4 flex items-center cursor-pointer transition-colors
                        focus:outline-none focus-visible:border-dojo-red
                        ${isDark ? 'hover:border-slate-500' : 'hover:border-stone-400'}
                        ${done ? 'opacity-60' : ''}`}
                    >
                      {/* Accent rail: 8px wide, full height, 14px of air after it. */}
                      <span
                        className="w-2 self-stretch rounded-[4px] mr-[14px] flex-shrink-0"
                        style={{ backgroundColor: rail }}
                        aria-hidden="true"
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-1">
                          <h3 className={`text-[16.5px] font-extrabold tracking-[-0.2px] flex-1 min-w-0
                            line-clamp-2 ${done ? 'line-through opacity-50' : ''}`}>
                            {task.title}
                          </h3>
                          {assignee && (
                            <span className={`text-[13px] ml-2 flex-shrink-0 ${colors.textSecondary}`}>
                              → {assignee}
                            </span>
                          )}
                        </div>

                        {/* Wraps rather than squeezing. Nowrap here is what
                            broke "Tomorrow" across two lines mid-word on the
                            phone: a second line is cheaper than illegible text. */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {task.status && (
                            <span
                              className="px-[9px] py-1 rounded-full text-[11px] font-extrabold
                                uppercase tracking-[0.4px]"
                              style={{
                                // The chip's real background is its own hue over
                                // the card, so that is what the text is measured
                                // against -- measuring against the card is why
                                // these read badly.
                                color: chipText(STATUS_COLOR[task.status] || '#8E8E93', card),
                                backgroundColor: `${STATUS_COLOR[task.status] || '#8E8E93'}20`,
                              }}
                            >
                              {statusLabel(task.status)}
                            </span>
                          )}

                          {task.deadline && (
                            <span className={`flex items-center gap-[3px] text-[12px] flex-shrink-0
                              ${overdue ? 'text-dojo-red' : `${colors.textSecondary} opacity-70`}`}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5">
                                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"
                                  strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4 22V15" strokeLinecap="round" />
                              </svg>
                              {formatDate(task.deadline)}
                              {formatTime(task.deadline) !== '12:00 AM' && `, ${formatTime(task.deadline)}`}
                            </span>
                          )}

                          {task.category && (
                            <span className={`flex items-center gap-[3px] text-[12px] opacity-70
                              flex-shrink-0 max-w-full ${colors.textSecondary}`}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
                                  strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span className="truncate">{task.category}</span>
                            </span>
                          )}

                          {watchers > 0 && (
                            <span className={`flex items-center gap-[2px] text-[12px] opacity-70 ${colors.textSecondary}`}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                  strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              {watchers}
                            </span>
                          )}
                        </div>

                        {(task.tag || task.ref || (task.progress > 0 && !done)) && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {task.tag && (
                              <span className="px-[9px] py-1 rounded-full text-[11px] font-extrabold
                                uppercase tracking-[0.4px] bg-dojo-red/[0.15] text-dojo-red">
                                #{task.tag}
                              </span>
                            )}
                            {/* A reference is an identifier from somewhere else —
                                a ticket or order number — so it is set in a
                                neutral chip rather than the tag's accent, and
                                left in its original case. */}
                            {task.ref && (
                              <span className={`px-[9px] py-1 rounded-full text-[11px] font-extrabold
                                tracking-[0.2px] tabular-nums ${isDark
                                  ? 'bg-slate-700/60 text-neutral-300'
                                  : 'bg-stone-200 text-neutral-600'}`}>
                                {task.ref}
                              </span>
                            )}
                            {task.progress > 0 && !done && (
                              <span className="px-[9px] py-1 rounded-full text-[11px] font-extrabold
                                uppercase tracking-[0.4px] bg-dojo-red/20 text-dojo-red tabular-nums">
                                {task.progress}%
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Ticking a task off should not require opening it.
                          Only shown where the rules allow the write. */}
                      {role.canWrite && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleComplete(task); }}
                          disabled={busyId === task.id}
                          aria-label={done ? `Mark ${task.title} not done` : `Mark ${task.title} done`}
                          className={`w-[38px] h-[38px] rounded-xl flex items-center justify-center
                            flex-shrink-0 self-start ml-3 ${raised} ${pressable} disabled:opacity-50
                            ${done
                              ? 'bg-dojo-green border-dojo-green text-white'
                              : `${colors.inputBg} ${isDark ? 'border-slate-600 text-slate-400' : 'border-stone-300 text-stone-400'}`}`}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* --------------------------- the editor --------------------------- */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={closeModal}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-[26px] font-black tracking-[-0.7px] flex-1">
                {editing ? 'Edit task' : 'New task'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}
                  ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <Field label="Title" className="mb-4">
              <input
                className={input}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="What needs doing?"
                autoFocus
              />
            </Field>

            <Field label="Notes" className="mb-4">
              <textarea
                className={`${input} min-h-[88px] resize-y`}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything worth remembering"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Category">
                <select
                  className={input}
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Tag">
                <input
                  className={input}
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value })}
                  placeholder="optional"
                />
              </Field>
            </div>

            <div className="mb-4">
              <Field label="Reference">
                <input
                  className={input}
                  value={form.ref}
                  onChange={(e) => setForm({ ...form, ref: e.target.value })}
                  placeholder="optional — ticket, order or doc number"
                />
              </Field>
            </div>

            {/* Scheduled window — the same pair the phone offers. Both go
                through fromDateTimeInputs, so what lands in Firestore is
                identical to the phone's toISOString() and a task scheduled on
                one can be edited on the other without the value shifting. */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Starts">
                <input
                  className={input}
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </Field>
              <Field label="Time">
                <input
                  className={input}
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  disabled={!form.startDate}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Ends">
                <input
                  className={input}
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </Field>
              <Field label="Time">
                <input
                  className={input}
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  disabled={!form.endDate}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Duration (minutes)">
                <input
                  className={input}
                  type="number"
                  min={0}
                  value={form.duration || ''}
                  onChange={(e) => setForm({ ...form, duration: Number(e.target.value) || 0 })}
                  placeholder="optional"
                />
              </Field>
              <Field label="Visibility">
                <label className={`flex items-center gap-3 rounded-[12px] border-2 px-3 h-[46px]
                  cursor-pointer ${isDark ? 'border-slate-700' : 'border-stone-300'}`}>
                  <input
                    type="checkbox"
                    checked={form.isPrivate}
                    onChange={(e) => setForm({ ...form, isPrivate: e.target.checked })}
                    className="w-4 h-4 accent-dojo-red"
                  />
                  <span className="text-[13.5px] font-extrabold">Private</span>
                </label>
              </Field>
            </div>

            {/* The browser shows the visitor's own date convention while they
                type, but the value exchanged is ISO, so there is nothing here
                for a locale to get wrong. */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Deadline">
                <input
                  type="date"
                  className={input}
                  value={form.deadlineDate}
                  onChange={(e) => setForm({ ...form, deadlineDate: e.target.value })}
                />
              </Field>
              <Field label="Time">
                <input
                  type="time"
                  className={input}
                  value={form.deadlineTime}
                  disabled={!form.deadlineDate}
                  onChange={(e) => setForm({ ...form, deadlineTime: e.target.value })}
                />
              </Field>
            </div>
            {form.deadlineDate && (
              <p className={`-mt-2 mb-4 text-[12px] font-semibold ${colors.textSecondary}`}>
                Due {formatDate(fromDateTimeInputs(form.deadlineDate, form.deadlineTime))}
                {form.deadlineTime && ` at ${formatTime(fromDateTimeInputs(form.deadlineDate, form.deadlineTime))}`}
              </p>
            )}

            {/* Sharing. Only people you are connected to can be picked --
                assigning to anyone else would write a uid that cannot read the
                task, so it would simply vanish for them. */}
            <Field label="Assign to" className="mb-4">
              {connections.length === 0 ? (
                <p className={`text-[13px] font-semibold ${colors.textSecondary}`}>
                  Connect with someone first and you can assign tasks to them.
                </p>
              ) : (
                <select
                  className={input}
                  value={form.assignedTo}
                  onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
                >
                  <option value="">Me</option>
                  {connections.map((c: any) => (
                    <option key={c.uid} value={c.uid}>
                      {c.displayName || c.email}
                    </option>
                  ))}
                </select>
              )}
              {!editing && form.assignedTo && (
                <p className={`text-[12px] font-semibold mt-1.5 ${colors.textSecondary}`}>
                  They'll get a request to accept it.
                </p>
              )}
            </Field>

            {connections.length > 0 && (
              <Field label="Watchers" className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {connections.map((c: any) => {
                    const on = form.watchers.includes(c.uid);
                    return (
                      <button
                        key={c.uid}
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          watchers: on
                            ? form.watchers.filter((w) => w !== c.uid)
                            : [...form.watchers, c.uid],
                        })}
                        aria-pressed={on}
                        className={`px-3 py-1.5 rounded-full border-2 text-[13px] font-extrabold transition-colors
                          ${on
                            ? 'bg-dojo-red/15 border-dojo-red text-dojo-red'
                            : `${colors.cardBorder} ${colors.textSecondary}`}`}
                      >
                        {c.displayName || c.email}
                      </button>
                    );
                  })}
                </div>
                <p className={`text-[12px] font-semibold mt-1.5 ${colors.textSecondary}`}>
                  Watchers can see the task and its updates, but not change it.
                </p>
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3 mb-2">
              <Field label="Remind me">
                <input
                  type="date"
                  className={input}
                  value={form.reminderDate}
                  onChange={(e) => setForm({ ...form, reminderDate: e.target.value })}
                />
              </Field>
              <Field label="Time">
                <input
                  type="time"
                  className={input}
                  value={form.reminderTime}
                  disabled={!form.reminderDate}
                  onChange={(e) => setForm({ ...form, reminderTime: e.target.value })}
                />
              </Field>
            </div>
            <p className={`text-[12px] font-semibold mb-4 ${colors.textSecondary}`}>
              {form.reminderDate
                ? `Reminding ${formatDate(fromDateTimeInputs(form.reminderDate, form.reminderTime))} at ${formatTime(fromDateTimeInputs(form.reminderDate, form.reminderTime))} — delivered to the taskDojo app.`
                : 'Optional. Reminders are delivered to the taskDojo app, not the browser.'}
            </p>

            {editing && (
              <Field label="Status" className="mb-4">
                <select
                  className={input}
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as Task['status'] })}
                >
                  {['not_started', 'in_progress', 'paused', 'completed'].map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Colour" className="mb-5">
              <div className="flex flex-wrap gap-2">
                {TASK_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.hex })}
                    aria-label={c.name}
                    aria-pressed={form.color === c.hex}
                    title={c.name}
                    className={`w-9 h-9 rounded-xl border-2 transition-transform
                      ${form.color === c.hex
                        ? 'scale-110 border-current'
                        : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create task'}
              </PrimaryButton>
              {editing && roleOn(editing).canDelete && (
                <SecondaryButton
                  onClick={() => setConfirmDelete(editing)}
                  disabled={saving}
                  className="!text-dojo-red"
                >
                  Delete
                </SecondaryButton>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Deleting is not undoable, so it asks. */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this task?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              “{confirmDelete.title}” will be removed for everyone who can see it. This can't be undone.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDelete(null)} className="flex-1">
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={() => remove(confirmDelete)}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
